import { useEffect, useMemo, useRef } from 'react';
import { X, Bell, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { useNotifications } from '../store/useStore';
import { cn } from '../lib/cn';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

const iconMap = {
  success: CheckCircle,
  error: AlertTriangle,
  warning: Bell,
  info: Info,
};

const variantClasses: Record<
  keyof typeof iconMap,
  { container: string; icon: string }
> = {
  success: {
    container: 'border-green-200 bg-green-50 text-green-900',
    icon: 'text-green-500',
  },
  error: {
    container: 'border-red-200 bg-red-50 text-red-900',
    icon: 'text-red-500',
  },
  warning: {
    container: 'border-yellow-200 bg-yellow-50 text-yellow-900',
    icon: 'text-yellow-500',
  },
  info: {
    container: 'border-slate-200 bg-slate-50 text-slate-900',
    icon: 'text-primary-500',
  },
};

const AUTO_DISMISS_MS = 3_000;

function Toast() {
  const { notifications, markNotificationAsRead } = useNotifications();
  const unreadNotifications = useMemo(
    () => notifications.filter((notification) => !notification.read),
    [notifications]
  );

  const scheduledRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const scheduled = scheduledRef.current;
    const unreadIds = new Set(unreadNotifications.map((n) => n.id));

    // Notifiche non più unread (lette o rimosse): elimina il timer residuo
    for (const [id, timerId] of scheduled) {
      if (!unreadIds.has(id)) {
        window.clearTimeout(timerId);
        scheduled.delete(id);
      }
    }

    // Pianifica solo i toast nuovi: un timer già avviato non va toccato
    // dal cambio di stato di altri toast, altrimenti l'auto-dismiss non parte mai.
    unreadNotifications
      .filter((notification) => !notification.persistent)
      .forEach((notification) => {
        if (scheduled.has(notification.id)) return;
        const timerId = window.setTimeout(() => {
          scheduled.delete(notification.id);
          markNotificationAsRead(notification.id);
        }, AUTO_DISMISS_MS);
        scheduled.set(notification.id, timerId);
      });
  }, [unreadNotifications, markNotificationAsRead]);

  useEffect(() => {
    const scheduled = scheduledRef.current;
    return () => {
      scheduled.forEach((timerId) => window.clearTimeout(timerId));
      scheduled.clear();
    };
  }, []);

  if (!unreadNotifications.length) {
    return null;
  }

  return (
    <div className="fixed left-3 right-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-[9999] flex flex-col gap-3 md:left-auto md:right-4 md:bottom-4 md:max-w-sm">
      {unreadNotifications.map((notification) => {
        const IconComponent = iconMap[notification.type];
        const classes = variantClasses[notification.type];
        const timestampLabel = formatDistanceToNow(
          new Date(notification.timestamp),
          { addSuffix: true, locale: it }
        );

        const handleActivate = () => {
          notification.action?.onClick();
        };

        return (
          <div
            key={notification.id}
            role={notification.action ? 'button' : undefined}
            tabIndex={notification.action ? 0 : undefined}
            onClick={notification.action ? handleActivate : undefined}
            onKeyDown={
              notification.action
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleActivate();
                    }
                  }
                : undefined
            }
            className={cn(
              'rounded-lg border p-4 shadow-lg backdrop-blur bg-opacity-95',
              classes.container,
              notification.action && 'cursor-pointer hover:brightness-[0.98] transition-[filter]'
            )}
          >
            <div className="flex items-start gap-3">
              <IconComponent className={cn('w-5 h-5 flex-shrink-0', classes.icon)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold leading-snug">
                      {notification.title}
                    </p>
                    {notification.message && (
                      <p className="text-sm mt-1 whitespace-pre-line break-words">{notification.message}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="text-current/70 hover:text-current transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      notification.onDismiss?.();
                      markNotificationAsRead(notification.id);
                    }}
                    aria-label="Chiudi notifica"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-xs opacity-70 mt-2">{timestampLabel}</div>
                {notification.action && (
                  <button
                    type="button"
                    className={cn(
                      "mt-3 px-4 py-2 rounded-md hover:opacity-90 transition-opacity font-medium text-sm",
                      notification.type === 'info' && "bg-primary-600 text-white hover:bg-primary-700",
                      notification.type === 'success' && "bg-green-600 text-white hover:bg-green-700",
                      notification.type === 'warning' && "bg-yellow-600 text-white hover:bg-yellow-700",
                      notification.type === 'error' && "bg-red-600 text-white hover:bg-red-700"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      notification.action?.onClick();
                    }}
                  >
                    {notification.action.label}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Toast;
